import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  ADVISOR_CONTEXTS,
  detectAdvisorContext,
  AdvisorContextType,
  TopicItem,
  SuggestionItem,
  PredictionMarketReply,
  PREDICTION_MARKET_LABELS
} from '../data/predictionMarketKnowledge';
import { PRODUCTS_AND_SERVICES_TOPICS } from '../data/predictionMarketKnowledge';
import { MarkdownRenderer } from './MarkdownRenderer';
import { StreamingMessage } from './StreamingMessage';
import { QualificationOptions } from './QualificationOptions';
import { YourTurnChip } from './YourTurnChip';
import { ContactFormCard } from './ContactFormCard';
import { useStreamingText } from '../useStreamingText';
import { VinfotechLogo } from './VinfotechLogo';
import { Mic, MicOff, Copy, ThumbsUp, ThumbsDown, X, Compass, Headset, ExternalLink, SquarePen } from 'lucide-react';
import {
  CUSTOM_OPTION_VALUE,
  QUERY_TYPE,
  hasAnswerButtons,
  isTypedQuestion,
  ChatAPI,
    type QualificationAnswerPayload,
  type QualificationOptionsPayload,
  type ContactFormPayload,
} from '../api';
import { getCurrentPageUrl, getCampaignRef, getSessionId, resetSessionId, stripQuestionFromMessage } from '../utils';
import { ChatStorage, type StoredMessage } from '../storage';
import type { AdvisorPageScope } from '../types';

const EMPTY_CONTACT_VALUES: Record<string, string> = {};

export const SHARE_MIN_USER_MESSAGES = 3;
const DEFAULT_ADVISOR_API_URL = 'https://ai-api.vinfotech.com/api';

export interface AdvisorMessage {
  role: 'user' | 'bot';
  text: string;
  streaming?: boolean;
  followups?: string[] | null;
  feedback?: 'up' | 'down' | null;
  sources?: Array<{ title: string; url: string }>;
}

export interface AdvisorSession {
  id: string;
  title: string;
  count: number;
  messages: AdvisorMessage[];
}

export interface ExpandedAdvisorViewProps {
  onMinimize?: () => void;
  onClose?: () => void;
  api?: ChatAPI | null;
  sessionId?: string;
  initialMessages?: AdvisorMessage[];
  onMessagesChange?: (messages: AdvisorMessage[]) => void;
  context?: 'general' | 'prediction-market' | string;
  companyName?: string;
  placeholderText?: string;
  initialSuggestions?: string[];
  dynamicSuggestions?: string[];
  onSuggestionsChange?: (suggestions: string[]) => void;
  initialQualification?: QualificationOptionsPayload | null;
  onQualificationChange?: (qualification: QualificationOptionsPayload | null) => void;
  initialYourTurn?: QualificationOptionsPayload | null;
  onYourTurnChange?: (yourTurn: QualificationOptionsPayload | null) => void;
  initialContactForm?: ContactFormPayload | null;
  onContactFormChange?: (form: ContactFormPayload | null) => void;
  initialContactValues?: Record<string, string>;
  onContactValuesChange?: (values: Record<string, string>) => void;
  initialQuery?: string;
  /**
   * Narrows the advisor to one page of the site (/advisor/<page>). It then
   * introduces itself as that product, opens on that page's questions, and
   * tells the backend every message is about that page. The conversation is
   * still the one shared with the widget. Absent, it is the general advisor.
   */
  scope?: AdvisorPageScope;
  /** The chat API to use when no `api` is passed — the one the site's widget uses on this host. */
  apiUrl?: string;
  /**
   * The Explore list: the site's Products & Services, from the header menu in
   * Keystatic. Absent or empty, the built-in prediction market topics show.
   */
  exploreTopics?: TopicItem[];
}

/**
 * The session the site's chat widget uses. The advisor shares it, together
 * with the widget's history format, so both show one conversation.
 */
const SHARED_SESSION_KEY = 'vinfotech_chat_session';

const BRAND = '#6720ff';

/** Desktop sidebar width limits when dragged, and where the chosen width is kept. */
const RAIL_MIN = 240;
const RAIL_MAX = 480;
const RAIL_WIDTH_KEY = 'vinfotech_advisor_sidebar_width';
/** Let go with the edge left of this and the sidebar closes, as in ChatGPT. */
const RAIL_COLLAPSE_AT = 170;
/** Width of the closed sidebar's icon strip. */
const RAIL_STRIP = 56;
const RAIL_DEFAULT = 'clamp(272px, 26vw, 340px)';

/** Dark label beside an icon on hover, as in ChatGPT. */
const Tip = ({ label, keys, below }: { label: string; keys?: string; below?: boolean }) => (
  <span className={`adv-tip${below ? ' adv-tip--below' : ''}`} aria-hidden="true">
    {label}
    {keys && <kbd>{keys}</kbd>}
  </span>
);


const ELLIPSIS: React.CSSProperties = { flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

const SidebarIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
    <rect x="2.5" y="3.5" width="15" height="13" rx="3" />
    <line x1="7.5" y1="3.5" x2="7.5" y2="16.5" />
  </svg>
);

const PlusIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    <line x1="9" y1="3" x2="9" y2="15" />
    <line x1="3" y1="9" x2="15" y2="9" />
  </svg>
);

const ChatIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="#a7a4b2" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M3 4.5h12v7H8l-3.5 3v-3H3z" />
  </svg>
);

/** Stored history, as the widget and the advisor both write it, to the advisor's messages. */
function toAdvisorMessages(cached: {
  messages: StoredMessage[];
  feedback?: Record<number, 'positive' | 'negative'>;
}): AdvisorMessage[] {
  return cached.messages.map((m, idx) => ({
    role: (m.type === 'user' ? 'user' : 'bot') as 'user' | 'bot',
    text: m.text,
    followups: m.followups && m.followups.length > 0 ? m.followups : null,
    feedback: cached.feedback?.[idx] === 'positive' ? 'up' : (cached.feedback?.[idx] === 'negative' ? 'down' : null)
  }));
}

export type DedicatedAdvisorProps = ExpandedAdvisorViewProps;

export const ExpandedAdvisorView: React.FC<ExpandedAdvisorViewProps> = ({
  onMinimize,
  onClose,
  api,
  sessionId,
  initialMessages = [],
  onMessagesChange,
  context: propContext,
  companyName,
  placeholderText,
  initialSuggestions,
  dynamicSuggestions,
  onSuggestionsChange,
  initialQualification = null,
  onQualificationChange,
  initialYourTurn = null,
  onYourTurnChange,
  initialContactForm = null,
  onContactFormChange,
  initialContactValues = EMPTY_CONTACT_VALUES,
  onContactValuesChange,
  initialQuery,
  scope,
  apiUrl,
  exploreTopics
}) => {
  const defaultApiRef = useRef<ChatAPI | null>(null);
  if (!defaultApiRef.current) {
    defaultApiRef.current = new ChatAPI(apiUrl || DEFAULT_ADVISOR_API_URL, 'sales-bot');
  }
  const effectiveApi = api || defaultApiRef.current;

  const storageRef = useRef<ChatStorage>(new ChatStorage('vinfotech_chat_', 180, true));
  const [currentSessionId, setCurrentSessionId] = useState<string>(() => sessionId || getSessionId(SHARED_SESSION_KEY));
  const effectiveSessionId = sessionId || currentSessionId;

  // Pre-load cached session state if available
  const cachedHistoryRef = useRef<any>(null);
  if (!cachedHistoryRef.current && typeof window !== 'undefined') {
    cachedHistoryRef.current = storageRef.current.loadChatHistory(effectiveSessionId);
  }

  const handleClose = onClose || (() => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  });
  // A scoped advisor is detected from the page it is about, not from /advisor/…,
  // which also makes the result the same on the server and in the browser.
  const [activeContextKey, setActiveContextKey] = useState<AdvisorContextType>(() =>
    scope ? detectAdvisorContext(propContext, scope.path, '', scope.name) : detectAdvisorContext(propContext)
  );

  useEffect(() => {
    if (propContext) {
      setActiveContextKey(detectAdvisorContext(propContext));
    }
  }, [propContext]);

  const [qualification, setQualification] = useState<QualificationOptionsPayload | null>(() => {
    if (initialQualification !== undefined && initialQualification !== null) return initialQualification;
    return cachedHistoryRef.current?.qualification || null;
  });
  const [yourTurn, setYourTurn] = useState<QualificationOptionsPayload | null>(() => {
    if (initialYourTurn !== undefined && initialYourTurn !== null) return initialYourTurn;
    return cachedHistoryRef.current?.yourTurn || null;
  });
  const [customFor, setCustomFor] = useState<QualificationOptionsPayload | null>(null);
  const [contactForm, setContactForm] = useState<ContactFormPayload | null>(() => {
    if (initialContactForm !== undefined && initialContactForm !== null) return initialContactForm;
    return cachedHistoryRef.current?.contactForm || null;
  });
  const [contactValues, setContactValues] = useState<Record<string, string>>(() => {
    if (initialContactValues && Object.keys(initialContactValues).length > 0) return initialContactValues;
    return cachedHistoryRef.current?.contactValues || EMPTY_CONTACT_VALUES;
  });

  useEffect(() => {
    if (initialYourTurn !== undefined) {
      setYourTurn(prev => (prev === initialYourTurn ? prev : initialYourTurn));
    }
  }, [initialYourTurn]);

  useEffect(() => {
    if (initialQualification !== undefined) {
      setQualification(prev => (prev === initialQualification ? prev : initialQualification));
    }
  }, [initialQualification]);

  useEffect(() => {
    if (initialContactForm !== undefined) {
      setContactForm(prev => (prev === initialContactForm ? prev : initialContactForm));
    }
  }, [initialContactForm]);

  useEffect(() => {
    if (initialContactValues && initialContactValues !== EMPTY_CONTACT_VALUES) {
      setContactValues(prev => (prev === initialContactValues ? prev : initialContactValues));
    }
  }, [initialContactValues]);

  const updateQualification = (payload: QualificationOptionsPayload | null) => {
    setQualification(payload);
    if (onQualificationChange) onQualificationChange(payload);
  };

  const updateYourTurn = (payload: QualificationOptionsPayload | null) => {
    setYourTurn(payload);
    if (onYourTurnChange) onYourTurnChange(payload);
  };

  const updateContactForm = (form: ContactFormPayload | null) => {
    setContactForm(form);
    if (onContactFormChange) onContactFormChange(form);
  };

  const updateContactValues = (vals: Record<string, string>) => {
    setContactValues(vals);
    if (onContactValuesChange) onContactValuesChange(vals);
  };

  const baseContext = ADVISOR_CONTEXTS[activeContextKey] || ADVISOR_CONTEXTS.general;
  // Scoped: the product's name, intro and questions replace the general ones.
  // The preset replies (the offline fallback) stay. Explore shows the site's
  // products when the page passes them.
  const activeContext = useMemo(() => {
    const topics = (exploreTopics && exploreTopics.length > 0) ? exploreTopics : PRODUCTS_AND_SERVICES_TOPICS;
    if (!scope) return { ...baseContext, topics };
    return {
      ...baseContext,
      topics,
      badgeLabel: scope.name,
      subheading: scope.intro || baseContext.subheading,
      suggestions: scope.suggestions.length > 0
        ? scope.suggestions.map((text, i) => ({ id: `scope-${i}`, text }))
        : baseContext.suggestions
    };
  }, [scope, baseContext, exploreTopics]);
  // Host + path, the format getCurrentPageUrl gives the widget on the page itself.
  const pageForBackend = () =>
    scope ? `${window.location.host}${scope.path}` : getCurrentPageUrl();
  const cleanCompanyName = companyName && companyName !== 'AI Assistant' && companyName !== 'AI ASSISTANT' && companyName !== 'Vinfotech AI'
    ? companyName
    : 'Vinfotech Product Expert';
  void cleanCompanyName;
  const isAwaitingTypedAnswer = Boolean(qualification && isTypedQuestion(qualification));

  const inputPlaceholder = customFor
    ? 'Type your own answer…'
    : isAwaitingTypedAnswer
    ? 'Type your answer…'
    : (placeholderText || 'Ask Advisor');

  // Resolve initial messages ensuring cached history or props are used and latest bot message has followups attached
  const resolvedInitialMessages = React.useMemo(() => {
    let sourceMessages = initialMessages;
    if (!sourceMessages || sourceMessages.length === 0) {
      const cached = cachedHistoryRef.current || (typeof window !== 'undefined' ? storageRef.current.loadChatHistory(effectiveSessionId) : null);
      if (cached && cached.messages && cached.messages.length > 0) {
        sourceMessages = toAdvisorMessages(cached);
      }
    }
    if (sourceMessages && sourceMessages.length > 0) {
      const copy = [...sourceMessages];
      const lastBotIdx = copy.map((m) => m.role).lastIndexOf('bot');
      if (lastBotIdx >= 0) {
        const lastBot = copy[lastBotIdx];
        if (!lastBot.followups || lastBot.followups.length === 0) {
          if (initialSuggestions && initialSuggestions.length > 0) {
            copy[lastBotIdx] = { ...lastBot, followups: initialSuggestions };
          } else {
            const fallback = activeContext.suggestions?.map((s) => s.text) || [];
            if (fallback.length > 0) {
              copy[lastBotIdx] = { ...lastBot, followups: fallback };
            }
          }
        }
      }
      return copy;
    }
    return [];
  }, [initialMessages, initialSuggestions, activeContext, effectiveSessionId]);

  const [messages, setMessages] = useState<AdvisorMessage[]>(resolvedInitialMessages);

  // Sync suggestions when initialSuggestions arrive or update
  useEffect(() => {
    if (initialSuggestions && initialSuggestions.length > 0) {
      setMessages((prev) => {
        const lastBotIdx = prev.map((m) => m.role).lastIndexOf('bot');
        if (lastBotIdx >= 0 && (!prev[lastBotIdx].followups || prev[lastBotIdx].followups?.length === 0)) {
          const copy = [...prev];
          copy[lastBotIdx] = { ...copy[lastBotIdx], followups: initialSuggestions };
          return copy;
        }
        return prev;
      });
    }
  }, [initialSuggestions]);

  const [history, setHistory] = useState<AdvisorSession[]>(() => {
    if (resolvedInitialMessages.length > 0) {
      const firstUser = resolvedInitialMessages.find((m) => m.role === 'user');
      const id = effectiveSessionId || 'session-1';
      return [
        {
          id,
          title: firstUser ? firstUser.text : 'Conversation',
          count: resolvedInitialMessages.filter((m) => m.role === 'user').length,
          messages: resolvedInitialMessages
        }
      ];
    }
    return [];
  });
  const [activeId, setActiveId] = useState<string | null>(
    resolvedInitialMessages.length > 0 ? (effectiveSessionId || 'session-1') : null
  );
  const [draft, setDraft] = useState('');
  const [thinking, setThinking] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [focused, setFocused] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareName, setShareName] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [shareSent, setShareSent] = useState(false);
  const [shareSending, setShareSending] = useState(false);
  const [shareFocus, setShareFocus] = useState<'name' | 'email' | null>(null);

  const shareInputStyle = (isFocused: boolean): React.CSSProperties => ({
    width: '100%',
    height: '46px',
    boxSizing: 'border-box',
    padding: '0 16px',
    border: isFocused ? '1px solid #cdbcfa' : '1px solid transparent',
    borderRadius: '12px',
    background: isFocused ? '#ffffff' : '#f4f2f8',
    fontFamily: 'inherit',
    fontSize: '15px',
    color: '#111018',
    outline: 'none',
    boxShadow: 'none',
    transition: 'border-color .16s ease, background .16s ease'
  });

  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [railOpen, setRailOpen] = useState(true);
  // Sidebar width the visitor dragged to; null keeps the default.
  const [railWidth, setRailWidth] = useState<number | null>(null);
  const [railDragging, setRailDragging] = useState(false);
  // Live width while the edge is being dragged; null when not dragging.
  const [railDragWidth, setRailDragWidth] = useState<number | null>(null);

  const loadingMessages = [
    'Analyzing your query...',
    'Searching the knowledge base...',
    'Processing information...',
    'Finalizing the answer...'
  ];
  const [loadingMessageIndex, setLoadingMessageIndex] = useState(0);
  const initialQuerySentRef = useRef(false);
  useEffect(() => {
    if (initialQuery && !initialQuerySentRef.current && initialQuery.trim().length > 0) {
      initialQuerySentRef.current = true;
      sendQuery(initialQuery.trim());
    }
  }, [initialQuery]);

  useEffect(() => {
    if (thinking && !streaming) {
      const interval = setInterval(() => {
        setLoadingMessageIndex((prev) => (prev + 1) % loadingMessages.length);
      }, 2000);
      return () => clearInterval(interval);
    } else {
      setLoadingMessageIndex(0);
    }
  }, [thinking, streaming]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const seqRef = useRef(1);
  const timerRef = useRef<any>(null);
  const intervalRef = useRef<any>(null);
  const typewriter = useStreamingText();
  const fullAnswerRef = useRef<string>('');
  const pendingFollowupsRef = useRef<string[] | null>(null);
  const isResponseCompleteRef = useRef<boolean>(false);
  const streamingIndexRef = useRef<number>(-1);

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Initialize Speech Recognition
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'en-US';

        recognition.onresult = (event: any) => {
          const transcript = event.results[0][0].transcript;
          setDraft((prev) => (prev ? prev + ' ' + transcript : transcript));
          setIsListening(false);
          // Auto-focus input after dictation
          if (inputRef.current) {
            inputRef.current.focus();
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 168)}px`;
          }
        };

        recognition.onerror = (event: any) => {
          console.error('Speech recognition error', event.error);
          setIsListening(false);
          if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
            alert('Voice input needs your microphone. Allow microphone access for this site in your browser, then try again.');
          }
        };

        recognition.onend = () => {
          setIsListening(false);
        };

        recognitionRef.current = recognition;
      }
    }
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch {}
      }
    };
  }, []);

  const toggleListening = () => {
    if (!recognitionRef.current) {
      alert('Speech recognition is not supported in this browser.');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        recognitionRef.current.start();
        setIsListening(true);
      } catch (err) {
        console.error('Failed to start speech recognition:', err);
      }
    }
  };

  // Resize measurement for mobile breakpoint
  useEffect(() => {
    const measure = () => {
      setIsMobile(window.innerWidth < 880);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  useEffect(() => {
    try {
      const saved = Number(window.localStorage.getItem(RAIL_WIDTH_KEY));
      if (saved >= RAIL_MIN && saved <= RAIL_MAX) setRailWidth(saved);
    } catch {}
  }, []);

  const saveRailWidth = (width: number | null) => {
    setRailWidth(width);
    try {
      if (width === null) window.localStorage.removeItem(RAIL_WIDTH_KEY);
      else window.localStorage.setItem(RAIL_WIDTH_KEY, String(width));
    } catch {}
  };

  // Drag the sidebar edge: the width follows the pointer all the way down to
  // the icon strip; on release it settles on the strip or a width, animated.
  const startRailDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const left = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect().left;
    const startX = e.clientX;
    const maxWidth = Math.min(RAIL_MAX, window.innerWidth * 0.5);
    let raw: number | null = null;
    setRailDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const move = (ev: PointerEvent) => {
      // A click on the edge is not a drag.
      if (raw === null && Math.abs(ev.clientX - startX) < 4) return;
      raw = Math.round(Math.min(maxWidth, Math.max(RAIL_STRIP, ev.clientX - left)));
      setRailDragWidth(raw);
    };
    const end = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', end);
      window.removeEventListener('pointercancel', end);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      setRailDragging(false);
      setRailDragWidth(null);
      if (raw === null) return;
      if (raw < RAIL_COLLAPSE_AT) {
        setRailOpen(false);
      } else {
        setRailOpen(true);
        saveRailWidth(Math.max(RAIL_MIN, raw));
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };

  // ⇧⌘S (Ctrl+Shift+S elsewhere) opens and closes the sidebar, as in ChatGPT.
  const [isMac, setIsMac] = useState(true);
  useEffect(() => {
    setIsMac(/Mac|iPhone|iPad/i.test(navigator.platform || navigator.userAgent));
    const onKey = (ev: KeyboardEvent) => {
      if ((ev.metaKey || ev.ctrlKey) && ev.shiftKey && ev.key.toLowerCase() === 's') {
        ev.preventDefault();
        if (window.innerWidth < 880) setMobileDrawerOpen((o) => !o);
        else setRailOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const toggleKeys = isMac ? '⇧⌘S' : 'Ctrl+Shift+S';

  // On desktop the composer moves from the welcome screen to the bottom once
  // the chat starts; keep the typing focus with it.
  const chatStarted = messages.length > 0;
  useEffect(() => {
    if (chatStarted && !isMobile && document.activeElement === document.body) {
      inputRef.current?.focus({ preventScroll: true });
    }
  }, [chatStarted, isMobile]);

  // Sync back to parent when messages change
  useEffect(() => {
    if (onMessagesChange) {
      onMessagesChange(messages);
    }
  }, [messages, onMessagesChange]);

  // Sync to shared ChatStorage whenever messages, qualification, or contact state changes
  useEffect(() => {
    if (!effectiveSessionId || typeof window === 'undefined') return;

    if (messages.length === 0) {
      if (!thinking && !streaming) {
        storageRef.current.clearChatHistory(effectiveSessionId);
      }
      return;
    }

    const messagesToStore: StoredMessage[] = messages
      .filter((m) => !m.streaming && m.text)
      .map((m) => ({
        type: (m.role === 'user' ? 'user' : 'assistant') as 'user' | 'assistant',
        text: m.text,
        followups: m.followups && m.followups.length > 0 ? m.followups : undefined
      }));

    if (messagesToStore.length === 0) return;

    const feedbackMap: Record<number, 'positive' | 'negative'> = {};
    messages.forEach((m, idx) => {
      if (m.feedback === 'up') feedbackMap[idx] = 'positive';
      else if (m.feedback === 'down') feedbackMap[idx] = 'negative';
    });

    const lastBotMsg = [...messages].reverse().find((m) => m.role === 'bot' && !m.streaming);
    const currentSuggestions = lastBotMsg?.followups || [];

    storageRef.current.saveChatHistory(effectiveSessionId, {
      messages: messagesToStore,
      suggestions: currentSuggestions,
      feedback: feedbackMap,
      qualification,
      contactForm,
      contactValues
    });
  }, [effectiveSessionId, messages, qualification, contactForm, contactValues, thinking, streaming]);

  // Listen for storage events from other tabs / the main site widget
  useEffect(() => {
    if (!effectiveSessionId || typeof window === 'undefined') return;

    // The widget's "new conversation" in another tab moves the shared session
    // on. Follow it — conversation, history and pending cards together — so
    // the advisor never keeps showing, or writes into, a chat the widget left.
    const followSharedSession = (): boolean => {
      if (sessionId) return false;
      let shared: string | null = null;
      try {
        shared = localStorage.getItem(SHARED_SESSION_KEY);
      } catch {}
      if (!shared || shared === effectiveSessionId) return false;
      try {
        sessionStorage.setItem(SHARED_SESSION_KEY, shared);
      } catch {}

      const cached = storageRef.current.loadChatHistory(shared);
      const loaded = toAdvisorMessages(cached);
      const firstUser = loaded.find((m) => m.role === 'user');
      stopTimers();
      setCurrentSessionId(shared);
      setMessages(loaded);
      setActiveId(loaded.length > 0 ? shared : null);
      setHistory(loaded.length > 0
        ? [{ id: shared, title: firstUser ? firstUser.text : 'Conversation', count: loaded.filter((m) => m.role === 'user').length, messages: loaded }]
        : []);
      setQualification(cached.qualification);
      setContactForm(cached.contactForm);
      setContactValues(cached.contactValues);
      setCustomFor(null);
      return true;
    };

    const syncFromStorage = () => {
      if (thinking || streaming) return;
      if (followSharedSession()) return;
      const cached = storageRef.current.loadChatHistory(effectiveSessionId);
      if (cached && cached.messages && cached.messages.length > 0) {
        const loaded = toAdvisorMessages(cached);

        setMessages((prev) => {
          if (
            prev.length !== loaded.length ||
            (prev.length > 0 && loaded.length > 0 && prev[prev.length - 1].text !== loaded[loaded.length - 1].text)
          ) {
            return loaded;
          }
          return prev;
        });

        if (cached.qualification !== undefined) setQualification(cached.qualification);
        if (cached.contactForm !== undefined) setContactForm(cached.contactForm);
        if (cached.contactValues) setContactValues(cached.contactValues);
      }
    };

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === `vinfotech_chat_history_${effectiveSessionId}` || e.key === SHARED_SESSION_KEY) {
        syncFromStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('focus', syncFromStorage);
    const handleVis = () => {
      if (document.visibilityState === 'visible') syncFromStorage();
    };
    document.addEventListener('visibilitychange', handleVis);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('focus', syncFromStorage);
      document.removeEventListener('visibilitychange', handleVis);
    };
  }, [effectiveSessionId, thinking, streaming]);

  const userScrolledUpRef = useRef(false);

  // Track if user manually scrolled up
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const handleScroll = () => {
      // If user is within 100px of bottom, consider them "at bottom"
      const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
      userScrolledUpRef.current = !isNearBottom;
    };

    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  // Auto scroll down unless user scrolled up
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (messages.length === 0) {
      el.scrollTop = 0;
      userScrolledUpRef.current = false;
      return;
    }
    if (!userScrolledUpRef.current) {
      requestAnimationFrame(() => {
        if (el && !userScrolledUpRef.current) {
          el.scrollTop = el.scrollHeight;
        }
      });
    }
  }, [messages, thinking, streaming, typewriter.text, qualification, contactForm]);

  // Clean up timers on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
      typewriter.reset();
    };
  }, []);

  const stopTimers = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
    typewriter.reset();
    isResponseCompleteRef.current = false;
  };

  // Finalize streaming once typewriter has caught up to the complete answer
  useEffect(() => {
    if (!streaming) return;
    if (!isResponseCompleteRef.current) return;
    if (typewriter.isTyping) return;
    if (!fullAnswerRef.current) return;

    if (typewriter.text !== fullAnswerRef.current) {
      typewriter.flush();
      return;
    }

    const finalText = fullAnswerRef.current;
    const followups = (pendingFollowupsRef.current && pendingFollowupsRef.current.length > 0)
      ? pendingFollowupsRef.current
      : (activeContext.suggestions?.map((s) => s.text) || []);
    const botIdx = streamingIndexRef.current;
    const currentId = activeId;

    setMessages((prev) => {
      if (botIdx < 0 || botIdx >= prev.length) return prev;
      const next = [...prev];
      next[botIdx] = {
        ...next[botIdx],
        text: finalText,
        streaming: false,
        followups: followups
      };
      if (currentId) {
        setHistory((h) => upsertHistory(h, currentId, next, false));
      }
      return next;
    });

    if (onSuggestionsChange && followups && followups.length > 0) {
      onSuggestionsChange(followups);
    }

    setStreaming(false);
    setThinking(false);
    streamingIndexRef.current = -1;
  }, [streaming, typewriter.isTyping, typewriter.text, activeId, activeContext, onSuggestionsChange]);

  // Safety fallback: if response is complete over network, guarantee flush within 2.5s
  useEffect(() => {
    if (!streaming || !isResponseCompleteRef.current) return;
    const safetyTimer = setTimeout(() => {
      if (streaming && isResponseCompleteRef.current) {
        typewriter.flush();
      }
    }, 2500);
    return () => clearTimeout(safetyTimer);
  }, [streaming]);

  const upsertHistory = (
    currentHistory: AdvisorSession[],
    id: string,
    msgs: AdvisorMessage[],
    toFront: boolean
  ): AdvisorSession[] => {
    const firstUser = msgs.find((m) => m.role === 'user');
    const entry: AdvisorSession = {
      id,
      title: firstUser ? firstUser.text : 'Conversation',
      count: msgs.filter((m) => m.role === 'user').length,
      messages: msgs
    };

    if (!toFront && currentHistory.some((h) => h.id === id)) {
      return currentHistory.map((h) => (h.id === id ? entry : h));
    }
    return [entry].concat(currentHistory.filter((h) => h.id !== id));
  };

  const streamPresetReply = (reply: PredictionMarketReply, targetIndex: number, currentId: string) => {
    setThinking(false);

    let isInstant = false;
    if (typeof window !== 'undefined') {
      try {
        isInstant = new URLSearchParams(window.location.search).get('instant') === 'true';
      } catch {}
    }

    if (isInstant) {
      setStreaming(false);
      setMessages((prev) => {
        const next = [...prev];
        next[targetIndex] = {
          role: 'bot',
          text: reply.text,
          streaming: false,
          followups: reply.followups
        };
        setHistory((h) => upsertHistory(h, currentId, next, false));
        return next;
      });
      return;
    }

    streamingIndexRef.current = targetIndex;
    fullAnswerRef.current = reply.text;
    pendingFollowupsRef.current = reply.followups || null;
    isResponseCompleteRef.current = true;
    setStreaming(true);
    typewriter.reset();
    typewriter.push(reply.text);
    typewriter.end();
  };

  const sendQuery = async (
    text: string,
    presetKey?: string,
    targetSessionId?: string,
    isNewSession = false,
    queryType?: number,
    qualificationAnswer?: QualificationAnswerPayload
  ) => {
    if (streaming || thinking) return;

    const currentId = targetSessionId || activeId || `session-${++seqRef.current}`;
    setActiveId(currentId);

    updateQualification(null);
    updateYourTurn(null);
    setCustomFor(null);
    updateContactForm(null);
    updateContactValues({});

    const newMsgs: AdvisorMessage[] = [...messages, { role: 'user', text }];
    setMessages(newMsgs);
    setDraft('');
    setThinking(true);
    setStreaming(false);
    userScrolledUpRef.current = false;
    setHistory((prev) => upsertHistory(prev, currentId, newMsgs, isNewSession));
    stopTimers();

    typewriter.reset();
    fullAnswerRef.current = '';
    pendingFollowupsRef.current = null;
    isResponseCompleteRef.current = false;
    const botIndex = newMsgs.length;
    streamingIndexRef.current = botIndex;

    const matchedKey = presetKey || activeContext.keyForQuery(text);
    const presetReply = activeContext.replies[matchedKey] || activeContext.replies.fallback;

    // Live stream from API if available
    if (effectiveApi && effectiveSessionId) {
      try {
        let apiSuggestions: string[] = [];
        let firstChunk = true;
        let receivedChunks = false;

        await effectiveApi.streamQuery(
          effectiveSessionId,
          text,
          {
            onResponseChunk: (chunk: string) => {
              receivedChunks = true;
              fullAnswerRef.current += chunk;
              if (firstChunk) {
                firstChunk = false;
                setThinking(false);
                setStreaming(true);
                setMessages([...newMsgs, { role: 'bot', text: '', streaming: true }]);
              }
              typewriter.push(chunk);
            },
            onCompleteResponse: (completeText: string) => {
              if (!receivedChunks || fullAnswerRef.current.length === 0) {
                fullAnswerRef.current = completeText;
                if (firstChunk) {
                  firstChunk = false;
                  setThinking(false);
                  setStreaming(true);
                  setMessages([...newMsgs, { role: 'bot', text: '', streaming: true }]);
                }
                typewriter.push(completeText);
              }
            },
            onSources: () => {
              setLoadingMessageIndex(1); // 'Searching the knowledge base...'
            },
            onMetadata: () => {
              setLoadingMessageIndex(2); // 'Processing information...'
            },
            onContactForm: (contactPayload: ContactFormPayload) => {
              updateContactForm(contactPayload);
              const seed: Record<string, string> = {};
              for (const f of contactPayload.fields) {
                if (f.value) seed[f.field_name] = f.value;
              }
              updateContactValues(seed);
            },
            onQualificationOptions: (payload: QualificationOptionsPayload) => {
              updateQualification(payload);
              pendingFollowupsRef.current = [];
              if (onSuggestionsChange) onSuggestionsChange([]);
            },
            onYourTurn: (payload: QualificationOptionsPayload) => {
              updateYourTurn(payload);
            },
            onSuggestedQuestions: (questions: string[]) => {
              if (questions && questions.length > 0) {
                apiSuggestions = questions;
                pendingFollowupsRef.current = questions;
                if (onSuggestionsChange) {
                  onSuggestionsChange(questions);
                }
              }
            },
            onDone: () => {
              isResponseCompleteRef.current = true;
              if (!pendingFollowupsRef.current || pendingFollowupsRef.current.length === 0) {
                const fallbackFollowups = apiSuggestions.length > 0
                  ? apiSuggestions
                  : (presetReply?.followups || (activeContext.suggestions?.map((s) => s.text) || []));
                pendingFollowupsRef.current = fallbackFollowups;
                if (onSuggestionsChange && fallbackFollowups.length > 0) {
                  onSuggestionsChange(fallbackFollowups);
                }
              }
              typewriter.end();
            },
            onError: (err: string) => {
              console.error('API Stream error:', err);
              if (receivedChunks && fullAnswerRef.current.length > 0) {
                isResponseCompleteRef.current = true;
                typewriter.end();
                return;
              }
              setThinking(false);
              setMessages([...newMsgs, { role: 'bot', text: '', streaming: true }]);
              streamPresetReply(presetReply, botIndex, currentId);
            }
          },
          queryType,
          pageForBackend(),
          getCampaignRef(),
          undefined,
          qualificationAnswer
        );
        return;
      } catch (e) {
        console.warn('Direct stream query failed:', e);
        if (fullAnswerRef.current.length > 0) {
          isResponseCompleteRef.current = true;
          typewriter.end();
          return;
        }
        setStreaming(false);
        setThinking(false);
        setMessages([...newMsgs, { role: 'bot', text: '', streaming: true }]);
        streamPresetReply(presetReply, newMsgs.length, currentId);
        return;
      }
    }

    // Default fallback preset if no API (immediate smooth typewriter streaming, no artificial delays)
    setThinking(false);
    setMessages([...newMsgs, { role: 'bot', text: '', streaming: true }]);
    streamPresetReply(presetReply, botIndex, currentId);
  };

  // Clicking an Explore topic: switch to existing thread or create a clean new one
  const handleSelectTopic = (topic: TopicItem) => {
    if (streaming || thinking) return;

    const topicTitle = `Tell me about ${topic.name}.`;
    if (activeId !== null) {
      const existing = history.find(
        (h) => h.title === topicTitle || h.title.toLowerCase().includes(topic.name.toLowerCase())
      );

      if (existing) {
        if (existing.id === activeId) return;
        stopTimers();
        setActiveId(existing.id);
        setMessages(existing.messages);
        setDraft('');
        return;
      }
    }

    stopTimers();
    const newId = `session-${++seqRef.current}`;
    sendQuery(topicTitle, topic.id, newId, true);
  };

  // Clicking a Suggestion card on the landing page
  const handleSelectSuggestion = (s: SuggestionItem) => {
    if (streaming || thinking) return;

    if (activeId !== null) {
      const existing = history.find((h) => h.title === s.text);
      if (existing) {
        if (existing.id === activeId) return;
        stopTimers();
        setActiveId(existing.id);
        setMessages(existing.messages);
        setDraft('');
        return;
      }
    }

    stopTimers();
    const newId = `session-${++seqRef.current}`;
    sendQuery(s.text, s.id, newId, true);
  };

  // Clicking a Follow-up suggestion inside a conversation
  const handleSelectFollowup = (text: string, key: string) => {
    if (streaming || thinking) return;
    const currentId = activeId || `session-${++seqRef.current}`;
    sendQuery(text, key, currentId, false);
  };

  // Typing a custom question in the composer
  const handleSendCustom = async (text: string) => {
    if (streaming || thinking) return;
    const currentId = activeId || `session-${++seqRef.current}`;
    const isNew = !activeId || messages.length === 0;

    const pending = customFor;
    const answer: QualificationAnswerPayload | undefined = pending
      ? {
          question_id: pending.question_id,
          answer_type: pending.answer_type,
          selected: [{ value: CUSTOM_OPTION_VALUE, label: 'Something else' }],
          is_custom: true,
          custom_text: text.trim(),
        }
      : undefined;

    setCustomFor(null);
    sendQuery(text, undefined, currentId, isNew, 1, answer);
  };

  const handleRate = (index: number, value: 'up' | 'down') => {
    setMessages((prev) => {
      const next = [...prev];
      const m = next[index];
      if (!m) return prev;
      next[index] = {
        ...m,
        feedback: m.feedback === value ? null : value
      };
      if (activeId) {
        setHistory((h) => upsertHistory(h, activeId, next, false));
      }
      return next;
    });
  };

  const [copiedMessageId, setCopiedMessageId] = useState<number | null>(null);

  const handleCopy = async (text: string, index: number) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        setCopiedMessageId(index);
        setTimeout(() => setCopiedMessageId(null), 2000);
      } else {
        throw new Error('Clipboard API unavailable');
      }
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      textArea.style.top = '0';
      textArea.contentEditable = 'true';
      textArea.readOnly = false;
      document.body.appendChild(textArea);

      if (navigator.userAgent.match(/ipad|iphone/i)) {
        const range = document.createRange();
        range.selectNodeContents(textArea);
        const selection = window.getSelection();
        if (selection) {
          selection.removeAllRanges();
          selection.addRange(range);
        }
        textArea.setSelectionRange(0, 999999);
      } else {
        textArea.select();
      }

      try {
        document.execCommand('copy');
        setCopiedMessageId(index);
        setTimeout(() => setCopiedMessageId(null), 2000);
      } catch (e) {
        console.error('Copy failed', e);
      }
      document.body.removeChild(textArea);
    }
  };

  const handleNewChat = () => {
    stopTimers();
    if (effectiveSessionId && typeof window !== 'undefined') {
      storageRef.current.clearChatHistory(effectiveSessionId);
    }
    const newSessionId = resetSessionId(SHARED_SESSION_KEY);
    setCurrentSessionId(newSessionId);
    setActiveId(null);
    setMessages([]);
    setDraft('');
    setThinking(false);
    setStreaming(false);
    setShareOpen(false);
    setShareSent(false);
    setShareSending(false);
    fullAnswerRef.current = '';
    pendingFollowupsRef.current = null;
    isResponseCompleteRef.current = false;
    streamingIndexRef.current = -1;
    updateQualification(null);
    setCustomFor(null);
    updateContactForm(null);
    updateContactValues({});
  };

  const handleSubmit = () => {
    const trimmed = draft.trim();
    if (!trimmed || thinking || streaming) return;
    if (isListening && recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch {}
      setIsListening(false);
    }
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
    }
    handleSendCustom(trimmed);
  };

  const handleSendSummary = async () => {
    if (!isEmailValid || shareSending) return;
    setShareSending(true);

    const summaryText = messages
      .map((m) => `${m.role === 'user' ? 'User' : 'Vinfotech Advisor'}: ${m.text}`)
      .join('\n\n');

    if (effectiveApi) {
      try {
        await effectiveApi.submitContactForm({
          session_id: activeId || effectiveSessionId || 'session-advisor',
          user_query: `Email conversation summary requested by ${shareName.trim()} (${shareEmail.trim()})`,
          contact_data: {
            name: shareName.trim(),
            email: shareEmail.trim(),
            intent: 'conversation_summary',
            summary: summaryText,
            source: 'expanded_advisor_view',
            page_url: typeof window !== 'undefined' ? window.location.href : ''
          }
        });
      } catch (err) {
        console.warn('submitContactForm API call failed, showing local confirmation:', err);
      }
    }

    setShareSending(false);
    setShareSent(true);
  };

  // Check URL params for automated testing/previews
  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const params = new URLSearchParams(window.location.search);
        const autoTopicId = params.get('autoTopic');
        if (autoTopicId) {
          const t = activeContext.topics.find((x) => x.id === autoTopicId);
          if (t) {
            handleSelectTopic(t);
          }
        }
        if (params.get('autoShare') === 'true') {
          setShareOpen(true);
        }
        if (params.get('shareFilled') === 'true') {
          setShareName('Harshit Joshi');
          setShareEmail('harshit.joshi@vinfotech.com');
        }
      } catch (e) {}
    }
  }, []);

  const busy = thinking || streaming;
  const canSend = draft.trim().length > 0 && !busy;
  const railFullWidth = railWidth ? `${railWidth}px` : RAIL_DEFAULT;
  // While dragging, the full sidebar shows until the edge nears the strip.
  const railShowsFull = railDragWidth !== null ? railDragWidth >= RAIL_COLLAPSE_AT : railOpen;
  const isEmpty = messages.length === 0;
  const hasMessages = messages.length > 0;
  const showShare = messages.length > 1 && !busy;
  const isEmailValid = Boolean(
    shareName.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(shareEmail.trim())
  );

  const activeTitle = history.find((h) => h.id === activeId)?.title;

  // One row of the sidebar or the phone drawer: New chat, a topic, a recent chat.
  const rowStyle = (big: boolean, active = false): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    width: '100%',
    minHeight: big ? '50px' : '44px',
    padding: big ? '0 12px' : '0 10px',
    border: 0,
    borderRadius: '12px',
    background: active ? '#ecebf3' : 'transparent',
    fontFamily: 'var(--font-inter), sans-serif',
    fontSize: big ? '17px' : '16px',
    fontWeight: active ? 600 : 500,
    color: '#111018',
    cursor: 'pointer',
    textAlign: 'left',
    boxSizing: 'border-box'
  });

  const navLabel = (text: string) => (
    <div style={{ padding: '0 10px 6px', fontSize: '14px', fontWeight: 500, color: '#8e8b99' }}>{text}</div>
  );

  const renderExplore = (big: boolean, onPick?: () => void) => (
    <nav style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: '0 0 auto' }}>
      {navLabel('Explore')}
      {activeContext.topics.map((topic) => (
        <button
          key={topic.id}
          type="button"
          className="adv-row"
          title={topic.desc}
          onClick={() => {
            handleSelectTopic(topic);
            onPick?.();
          }}
          style={{ ...rowStyle(big, activeTitle === `Tell me about ${topic.name}.`), fontSize: '14px', minHeight: '22px' }}
        >
          <span
            aria-hidden="true"
            style={{
              width: '5px',
              height: '5px',
              borderRadius: '50%',
              flexShrink: 0,
              margin: '0 6px 0 7px',
              background: BRAND
            }}
          />
          <span className="adv-topic-name" style={{ flex: 1, minWidth: 0, padding: '10px 0', lineHeight: 1.3 }}>{topic.name}</span>
        </button>
      ))}
    </nav>
  );

  const renderRecents = (onPick?: () => void) =>
    history.length > 0 && (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', flex: '0 0 auto' }}>
        {navLabel('Recent')}
        {history.map((h) => {
          const isActive = h.id === activeId;
          return (
            <button
              key={h.id}
              type="button"
              className="adv-row"
              title={h.title}
              onClick={() => {
                onPick?.();
                if (busy || isActive) return;
                stopTimers();
                setMessages(h.messages);
                setActiveId(h.id);
                setDraft('');
              }}
              style={{
                ...rowStyle(false, isActive),
                background: isActive ? '#fafaff' : 'transparent',
                minHeight: '36px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 400,
                color: isActive ? '#111018' : '#4a4756'
              }}
            >
              <span style={ELLIPSIS}>{h.title}</span>
            </button>
          );
        })}
      </div>
    );

  // Same "Talk to sales" button as the expanded chat widget, centred and as
  // wide as the sidebar or drawer it sits in.
  const renderTalkToSales = () => (
    <a
      href="/contact"
      className="adv-cta"
      style={{
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 'calc(100% - 20px)',
        height: '46px',
        margin: '0 auto',
        padding: '0 14px',
        borderRadius: '12px',
        background: BRAND,
        color: '#ffffff',
        fontFamily: 'var(--font-inter), sans-serif',
        fontSize: '14px',
        fontWeight: 700,
        lineHeight: 1,
        letterSpacing: '-0.01em',
        textDecoration: 'none',
        boxSizing: 'border-box'
      }}
    >
      Talk to sales
    </a>
  );

  // Scoped advisors: a small link back to the page they are about, centred under Talk to sales.
  const renderProductLink = () => scope && (
    <a
      href={scope.path}
      target="_blank"
      rel="noopener"
      title="Opens the product page in a new window"
      className="adv-small-link"
      style={{
        flex: '0 0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        width: 'calc(100% - 20px)',
        margin: '0 auto',
        fontSize: '13px',
        fontWeight: 600,
        color: '#635c78',
        textDecoration: 'none'
      }}
    >
      View product page
      <svg width="12" height="12" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
        <path d="M5.5 2.5H2.5v9h9v-3" />
        <path d="M8.5 2.5h3v3" />
        <path d="M11.5 2.5L6.5 7.5" />
      </svg>
    </a>
  );

  // Icon, title and line under it, shared by the closed and open email card.
  const shareHeading = (
    <span style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '10px' : '12px', minWidth: 0 }}>
      <span
        aria-hidden="true"
        style={{
          width: '36px',
          height: '36px',
          flex: '0 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '10px',
          border: '1px solid #e2e0ea',
          background: '#ecebf3',
          color: BRAND,
          boxSizing: 'border-box'
        }}
      >
        <svg width="21" height="21" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2.5" y="4.5" width="15" height="11" rx="2" />
          <path d="M3 6l7 5 7-5" />
        </svg>
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
        <span style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 600, color: '#111018', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Get this chat in your inbox</span>
        <span style={{ fontSize: '13px', fontWeight: 400, color: '#6b6878', lineHeight: 1.3 }}>A short summary, no spam.</span>
      </span>
    </span>
  );

  const iconButton: React.CSSProperties = {
    width: '44px',
    height: '44px',
    flexShrink: 0,
    borderRadius: '10px',
    border: 0,
    background: 'transparent',
    color: '#5f5c6b',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0
  };

  const composer = (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '6px',
        padding: '8px 8px 8px 18px',
        borderRadius: '28px',
        background: '#ffffff',
        border: `1px solid ${focused ? '#cdbcfa' : '#e6e5eb'}`,
        boxShadow: '0 10px 36px -12px rgba(30, 20, 70, 0.16)',
        transition: 'border-color .2s ease'
      }}
    >
      <textarea
        ref={inputRef}
        rows={1}
        placeholder={hasMessages && !customFor && !isAwaitingTypedAnswer ? 'Ask a follow-up' : inputPlaceholder}
        value={draft}
        className="advisor-textarea"
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const el = e.target as HTMLTextAreaElement;
          el.style.height = 'auto';
          const nextH = Math.min(el.scrollHeight, 168);
          el.style.height = `${nextH}px`;
          el.style.overflowY = el.scrollHeight > 168 ? 'auto' : 'hidden';
          setDraft(el.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSubmit();
          }
        }}
        style={{
          flex: 1,
          minWidth: 0,
          border: 0,
          outline: 'none',
          resize: 'none',
          overflowY: 'hidden',
          overflowX: 'hidden',
          background: 'transparent',
          fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          fontSize: isMobile ? '17px' : '18px',
          fontWeight: 400,
          lineHeight: '26px',
          color: '#111018',
          padding: '9px 0',
          maxHeight: '168px',
          boxSizing: 'border-box'
        }}
      />
      <button
        type="button"
        className="adv-icon-btn"
        onClick={toggleListening}
        disabled={busy}
        title={isListening ? 'Listening... click to stop' : 'Voice input'}
        aria-label={isListening ? 'Stop voice input' : 'Voice input'}
        style={{
          ...iconButton,
          borderRadius: '50%',
          background: isListening ? '#fee2e2' : 'transparent',
          color: isListening ? '#dc2626' : '#8a83a3',
          cursor: busy ? 'not-allowed' : 'pointer',
          opacity: busy ? 0.4 : 1
        }}
      >
        {isListening ? <MicOff style={{ width: '19px', height: '19px' }} /> : <Mic style={{ width: '19px', height: '19px' }} />}
      </button>
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSend}
        aria-label="Send message"
        style={{
          ...iconButton,
          borderRadius: '50%',
          background: canSend ? BRAND : '#d6d4de',
          color: '#ffffff',
          cursor: canSend ? 'pointer' : 'default',
          transition: 'background .16s ease'
        }}
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <line x1="9" y1="15" x2="9" y2="3" />
          <polyline points="4,8 9,3 14,8" />
        </svg>
      </button>
    </div>
  );

  return (
    <div
      className="advisor-workspace"
      style={{
        display: 'flex',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
        background: '#ffffff',
        fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#111018'
      }}
    >
      {/* ─── SIDEBAR (desktop): one panel whose width animates between the
          full sidebar and the icon strip, resizable by dragging its edge ─── */}
      <aside
        className="adv-rail"
        style={{
          width: railDragWidth !== null ? `${railDragWidth}px` : railOpen ? railFullWidth : `${RAIL_STRIP}px`,
          flex: '0 0 auto',
          position: 'relative',
          // Above the chat's composer bar (50), so the strip's tooltips show over it.
          zIndex: 60,
          display: isMobile ? 'none' : 'block',
          background: '#ffffff',
          borderRight: '1px solid #eeedf2',
          boxSizing: 'border-box',
          transition: railDragWidth !== null ? 'none' : 'width .24s cubic-bezier(.2, 0, 0, 1)'
        }}
      >
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={railShowsFull ? 'Resize sidebar' : 'Drag to open sidebar'}
          title={railShowsFull ? 'Drag to resize' : 'Drag to open'}
          className={`adv-rail-handle${railDragging ? ' is-dragging' : ''}`}
          onPointerDown={startRailDrag}
          onDoubleClick={() => railShowsFull && saveRailWidth(null)}
        />

        {/* Full sidebar. Keeps its own width while the panel animates, so
            nothing reflows; the panel clips it. */}
        <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
          <div
            aria-hidden={!railShowsFull}
            style={{
              width: railDragWidth !== null ? `${Math.max(RAIL_MIN, railDragWidth)}px` : railFullWidth,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: '22px',
              padding: '14px 12px 16px',
              boxSizing: 'border-box',
              opacity: railShowsFull ? 1 : 0,
              visibility: railShowsFull ? 'visible' : 'hidden',
              transition: railShowsFull ? 'opacity .18s ease .06s, visibility 0s' : 'opacity .12s ease, visibility 0s .12s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0 0 10px', flex: '0 0 auto' }}>
              <a href="/" title="Go to Vinfotech Home" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
                <VinfotechLogo height={22} />
              </a>
              <button type="button" className="adv-icon-btn adv-has-tip" onClick={() => setRailOpen(false)} aria-label="Close sidebar" style={{ ...iconButton, width: '38px', height: '38px' }}>
                <SidebarIcon />
                <Tip label="Close sidebar" keys={toggleKeys} below />
              </button>
            </div>

            <div
              data-scrollable="true"
              className="vw-chat-messages-container"
              style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', overscrollBehaviorY: 'contain', display: 'flex', flexDirection: 'column', gap: '24px' }}
            >
              <button type="button" className="adv-row" onClick={handleNewChat} style={{ ...rowStyle(false), flex: '0 0 auto' }}>
                <PlusIcon />
                New chat
              </button>
              {renderExplore(false)}
              {renderRecents()}
            </div>

            <div style={{ flex: '0 0 auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {renderTalkToSales()}
              {renderProductLink()}
            </div>
          </div>
        </div>

        {/* Icon strip, shown when the sidebar is closed */}
        <nav
          aria-label="Sidebar"
          aria-hidden={railShowsFull}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${RAIL_STRIP}px`,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '6px',
            padding: '14px 0 16px',
            boxSizing: 'border-box',
            opacity: railShowsFull ? 0 : 1,
            visibility: railShowsFull ? 'hidden' : 'visible',
            transition: railShowsFull ? 'opacity .1s ease, visibility 0s .1s' : 'opacity .18s ease .08s, visibility 0s'
          }}
        >
          <button
            type="button"
            className="adv-icon-btn adv-has-tip adv-strip-logo"
            onClick={() => setRailOpen(true)}
            aria-label="Open sidebar"
            style={{ ...iconButton, width: '40px', height: '40px', marginBottom: '14px' }}
          >
            <span className="adv-strip-mark"><VinfotechLogo markOnly height={24} /></span>
            <span className="adv-strip-toggle"><SidebarIcon /></span>
            <Tip label="Open sidebar" keys={toggleKeys} />
          </button>
          <button type="button" className="adv-icon-btn adv-has-tip" onClick={handleNewChat} aria-label="New chat" style={{ ...iconButton, width: '40px', height: '40px', color: '#111018' }}>
            <SquarePen size={18} strokeWidth={1.7} />
            <Tip label="New chat" />
          </button>
          <button type="button" className="adv-icon-btn adv-has-tip" onClick={() => setRailOpen(true)} aria-label="Explore" style={{ ...iconButton, width: '40px', height: '40px', color: '#111018' }}>
            {/* Lucide draws the compass at 20/24 of its box: 22 gives an 18px circle. */}
            <Compass size={22} strokeWidth={1.6} />
            <Tip label="Explore" />
          </button>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
            {/* Buttons, not links: a hovered link makes the browser show its
                address over this corner, hiding the button and its label. */}
            {scope && (
              <button
                type="button"
                className="adv-icon-btn adv-has-tip"
                onClick={() => window.open(scope.path, '_blank', 'noopener')}
                aria-label="View product page (opens in a new window)"
                style={{ ...iconButton, width: '40px', height: '40px' }}
              >
                <ExternalLink size={17} strokeWidth={1.7} />
                <Tip label="View product page" />
              </button>
            )}
            <button
              type="button"
              className="adv-cta adv-has-tip"
              onClick={() => { window.location.href = '/contact'; }}
              aria-label="Talk to sales"
              style={{ ...iconButton, width: '40px', height: '40px', borderRadius: '12px', background: BRAND, color: '#ffffff' }}
            >
              <Headset size={18} strokeWidth={1.8} />
              <Tip label="Talk to sales" />
            </button>
          </div>
        </nav>
      </aside>

      {/* ─── MAIN WORKSPACE ─── */}
      <main style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', position: 'relative', background: '#ffffff' }}>
        {/* Phone drawer: slides in from the left over a dimmed page */}
        {isMobile && mobileDrawerOpen && (
          <>
            <div
              onClick={() => setMobileDrawerOpen(false)}
              style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(17, 16, 24, 0.3)', animation: 'fadeIn .2s ease both' }}
            />
            <aside
              role="dialog"
              aria-label="Menu"
              className="adv-drawer"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                bottom: 0,
                zIndex: 201,
                width: 'min(86%, 320px)',
                overflowY: 'auto',
                overscrollBehaviorY: 'contain',
                display: 'flex',
                flexDirection: 'column',
                gap: '24px',
                padding: '14px 12px calc(16px + env(safe-area-inset-bottom, 0px))',
                background: '#ffffff',
                boxShadow: '0 10px 40px rgba(17, 16, 24, 0.18)',
                boxSizing: 'border-box',
                animation: 'slideInLeft .22s ease both'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0 0 12px', flex: '0 0 auto' }}>
                <a href="/" title="Go to Vinfotech Home" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
                  <VinfotechLogo height={20} />
                </a>
                <button type="button" onClick={() => setMobileDrawerOpen(false)} aria-label="Close menu" style={{ ...iconButton, color: '#5f5c6b' }}>
                  <X size={24} />
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  handleNewChat();
                  setMobileDrawerOpen(false);
                }}
                className="adv-row"
                style={{ ...rowStyle(true), flex: '0 0 auto', fontSize: '14px' }}
              >
                <PlusIcon />
                New chat
              </button>
              {renderExplore(true, () => setMobileDrawerOpen(false))}
              {renderRecents(() => setMobileDrawerOpen(false))}
              <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {renderTalkToSales()}
                {renderProductLink()}
              </div>
            </aside>
          </>
        )}

        {/* Top bar: opens the drawer or the closed sidebar, starts a new chat on phones */}
        {(isMobile || onMinimize || onClose) && (
          <header
            style={{
              height: '60px',
              flex: '0 0 60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              padding: '0 10px',
              background: '#ffffff',
              zIndex: 40,
              boxSizing: 'border-box'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', minWidth: '44px' }}>
              {isMobile && (
                <button
                  type="button"
                  className="adv-icon-btn"
                  onClick={() => setMobileDrawerOpen(true)}
                  aria-label="Open sidebar"
                  title="Open sidebar"
                  style={iconButton}
                >
                  <SidebarIcon />
                </button>
              )}
            </div>
            {isMobile && (
              <a href="/" title="Go to Vinfotech Home" style={{ display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}>
                <VinfotechLogo height={20} />
              </a>
            )}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', minWidth: '44px' }}>
              {isMobile && (
                <button type="button" className="adv-icon-btn" onClick={handleNewChat} aria-label="New chat" title="New chat" style={{ ...iconButton, color: '#111018' }}>
                  <PlusIcon size={20} />
                </button>
              )}
              {!isMobile && onMinimize && (
                <button type="button" className="adv-icon-btn" onClick={onMinimize} aria-label="Minimize to widget" title="Minimize to widget" style={iconButton}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="14" y1="10" x2="21" y2="3" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                  </svg>
                </button>
              )}
              {onClose && (
                <button type="button" className="adv-icon-btn" onClick={handleClose} aria-label="Close" title="Close" style={iconButton}>
                  <X size={18} />
                </button>
              )}
            </div>
          </header>
        )}

        {/* Scrollable Chat / Landing Body */}
        <div
          ref={scrollRef}
          data-scrollable="true"
          className="vw-chat-messages-container"
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehaviorY: 'contain',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            background: '#ffffff'
          }}
        >
          {/* EMPTY LANDING STATE */}
          {isEmpty && (
            <div
              className="adv-empty"
              style={{
                flex: 1,
                width: '100%',
                maxWidth: '760px',
                margin: '0 auto',
                padding: isMobile ? '16px 18px' : '24px 18px 8vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '28px',
                boxSizing: 'border-box'
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '14px',
                  textAlign: 'center',
                  animation: 'fadeUp .5s ease both'
                }}
              >
                <h1
                  className="adv-h1"
                  style={{
                    margin: 0,
                    fontFamily: 'var(--font-inter), sans-serif',
                    fontSize: isMobile ? '30px' : 'clamp(32px, 3.2vw, 42px)',
                    lineHeight: 1.1,
                    fontWeight: 600,
                    letterSpacing: '-0.035em',
                    color: '#111018',
                    textTransform: 'none'
                  }}
                >
                  {activeContext.heading}
                </h1>
              </div>

              {/* Desktop: the composer sits in the middle, the questions below it.
                  Phones show the questions as cards above the pinned composer. */}
              {!isMobile && (
                <>
                  <div style={{ animation: 'fadeUp .5s ease .08s both' }}>{composer}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', animation: 'fadeUp .5s ease .14s both' }}>
                    {activeContext.suggestions.map((s) => (
                      <button
                        key={s.id}
                        type="button"
                        className="adv-sugg"
                        onClick={() => handleSelectSuggestion(s)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '14px',
                          minHeight: '52px',
                          padding: '10px 16px',
                          borderRadius: '14px',
                          border: 0,
                          background: 'transparent',
                          fontFamily: 'var(--font-inter), sans-serif',
                          fontSize: '17px',
                          color: '#4a4756',
                          cursor: 'pointer',
                          textAlign: 'left'
                        }}
                      >
                        <ChatIcon />
                        <span style={{ flex: 1 }}>{s.text}</span>
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ACTIVE THREAD */}
          {hasMessages && (
            <div
              style={{
                width: '100%',
                maxWidth: '764px',
                margin: '0 auto',
                boxSizing: 'border-box',
                padding: isMobile ? '12px 18px 28px' : '24px 22px 28px',
                display: 'flex',
                flexDirection: 'column',
                gap: '36px'
              }}
            >
              {messages.map((m, idx) => (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', animation: 'fadeUp .4s ease both' }}>
                  {/* User message */}
                  {m.role === 'user' && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <div
                        style={{
                          maxWidth: isMobile ? '85%' : '80%',
                          padding: '12px 20px',
                          borderRadius: '22px',
                          background: '#fafaff',
                          color: '#111018',
                          fontSize: '16px',
                          fontWeight: 400,
                          lineHeight: 1.5,
                          letterSpacing: '-0.01em',
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          boxShadow: 'none'
                        }}
                      >
                        {m.text}
                      </div>
                    </div>
                  )}

                  {/* Advisor bot message */}
                  {m.role === 'bot' && (
                    <div style={{ width: '100%' }}>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: 400,
                          lineHeight: 1.625,
                          color: '#111827',
                          whiteSpace: 'normal',
                          letterSpacing: '-0.005em'
                        }}
                      >
                        {m.streaming && idx === messages.length - 1 ? (
                          <>
                            <StreamingMessage
                              content={typewriter.text || m.text}
                              isExpanded={false}
                              isDark={false}
                              primaryColor="#6720ff"
                            />
                            {typewriter.isTyping && (
                              <span
                                className="vw-caret"
                                style={{
                                  display: 'inline-block',
                                  width: '7px',
                                  height: '16px',
                                  marginLeft: '3px',
                                  verticalAlign: '-2px',
                                  background: '#6720ff',
                                  borderRadius: '2px',
                                  animation: 'caret 1s steps(1) infinite'
                                }}
                              />
                            )}
                          </>
                        ) : (
                          <MarkdownRenderer
                            content={
                              idx === messages.length - 1 && qualification && hasAnswerButtons(qualification) && !thinking && !streaming && qualification.question
                                ? stripQuestionFromMessage(m.text, qualification.question)
                                : m.text
                            }
                            isExpanded={false}
                            isDark={false}
                            primaryColor="#6720ff"
                          />
                        )}
                      </div>

                      {/* Contact Form Card */}
                      {idx === messages.length - 1 && contactForm && !thinking && !streaming && (
                        <div style={{ marginTop: '14px', width: '100%', maxWidth: '580px' }}>
                          <ContactFormCard
                            contact={contactForm}
                            theme="light"
                            primaryColor="#6720ff"
                            values={contactValues}
                            onChange={updateContactValues}
                            onSubmit={async (values) => {
                              if (!effectiveApi || !effectiveSessionId) return;
                              const result = await effectiveApi.submitContactForm({
                                session_id: effectiveSessionId,
                                user_query: contactForm.original_query,
                                contact_data: values,
                              });
                              if (!result.success) {
                                throw new Error(result.error || 'submit failed');
                              }
                            }}
                          />
                        </div>
                      )}

                      {/* Qualification Tappable Options */}
                      {idx === messages.length - 1 && qualification && hasAnswerButtons(qualification) && !thinking && !streaming && (
                        <div style={{ marginTop: '14px', width: '100%', maxWidth: '640px' }}>
                          <QualificationOptions
                            data={qualification}
                            theme="light"
                            primaryColor="#6720ff"
                            disabled={thinking || streaming}
                            expanded={true}
                            onAnswer={(text, payload) => {
                              sendQuery(text, undefined, undefined, false, 1, payload);
                            }}
                            onChooseCustom={(data) => {
                              setCustomFor(data);
                              if (data) {
                                inputRef.current?.focus();
                              } else {
                                setDraft('');
                              }
                            }}
                            onSkip={() => {
                              updateQualification(null);
                              setCustomFor(null);
                            }}
                          />
                        </div>
                      )}

                      {/* Thumbs Feedback & Follow-ups */}
                      {!m.streaming && (
                        <>
                          {/* Rating and Copy buttons */}
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              marginTop: '16px',
                              animation: 'smoothFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both'
                            }}
                          >
                            <button
                              type="button"
                              onClick={() => handleCopy(m.text, idx)}
                              title={copiedMessageId === idx ? 'Copied' : 'Copy'}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '4px',
                                height: '34px',
                                padding: copiedMessageId === idx ? '0 8px' : '0 7px',
                                border: 0,
                                borderRadius: '9px',
                                background: copiedMessageId === idx ? '#f0eaff' : 'transparent',
                                color: copiedMessageId === idx ? '#6720ff' : '#8a83a3',
                                cursor: 'pointer',
                                transition: 'all .16s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (copiedMessageId !== idx) {
                                  (e.currentTarget as HTMLElement).style.background = '#f4efff';
                                  (e.currentTarget as HTMLElement).style.color = '#6720ff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (copiedMessageId !== idx) {
                                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                                  (e.currentTarget as HTMLElement).style.color = '#8a83a3';
                                }
                              }}
                            >
                              {copiedMessageId === idx && (
                                <span style={{ fontSize: '11px', fontWeight: 600, color: '#6720ff', marginRight: '2px' }}>
                                  Copied
                                </span>
                              )}
                              <Copy style={{ width: '17px', height: '17px', strokeWidth: 1.5 }} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRate(idx, 'up')}
                              title="Helpful"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '34px',
                                height: '34px',
                                border: 0,
                                borderRadius: '9px',
                                background: m.feedback === 'up' ? '#f0eaff' : 'transparent',
                                color: m.feedback === 'up' ? '#6720ff' : '#8a83a3',
                                cursor: 'pointer',
                                transition: 'all .16s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (m.feedback !== 'up') {
                                  (e.currentTarget as HTMLElement).style.background = '#f4efff';
                                  (e.currentTarget as HTMLElement).style.color = '#6720ff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (m.feedback !== 'up') {
                                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                                  (e.currentTarget as HTMLElement).style.color = '#8a83a3';
                                }
                              }}
                            >
                              <ThumbsUp style={{ width: '17px', height: '17px', strokeWidth: 1.5 }} />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRate(idx, 'down')}
                              title="Not helpful"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '34px',
                                height: '34px',
                                border: 0,
                                borderRadius: '9px',
                                background: m.feedback === 'down' ? '#f0eaff' : 'transparent',
                                color: m.feedback === 'down' ? '#6720ff' : '#8a83a3',
                                cursor: 'pointer',
                                transition: 'all .16s ease'
                              }}
                              onMouseEnter={(e) => {
                                if (m.feedback !== 'down') {
                                  (e.currentTarget as HTMLElement).style.background = '#f4efff';
                                  (e.currentTarget as HTMLElement).style.color = '#6720ff';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (m.feedback !== 'down') {
                                  (e.currentTarget as HTMLElement).style.background = 'transparent';
                                  (e.currentTarget as HTMLElement).style.color = '#8a83a3';
                                }
                              }}
                            >
                              <ThumbsDown style={{ width: '17px', height: '17px', strokeWidth: 1.5 }} />
                            </button>
                          </div>

                          {/* Followup suggestions matching non-expanded view */}
                          {(() => {
                            const isLastBot = idx === messages.length - 1;
                            if (isLastBot && (qualification || contactForm)) return null;

                            const followupsToRender = (m.followups && m.followups.length > 0)
                              ? m.followups
                              : (isLastBot ? ((initialSuggestions && initialSuggestions.length > 0) ? initialSuggestions : ((dynamicSuggestions && dynamicSuggestions.length > 0) ? dynamicSuggestions : (activeContext.suggestions?.map((s) => s.text) || []))) : []);

                            const turnChip = isLastBot && yourTurn && !thinking && !streaming ? yourTurn : null;
                            if ((!followupsToRender || followupsToRender.length === 0) && !turnChip) return null;

                              return (
                                <div
                                  style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    gap: '10px',
                                    marginTop: '20px',
                                    width: '100%',
                                    animation: 'smoothFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both'
                                  }}
                                >
                                  <p
                                    style={{
                                      fontFamily: 'var(--font-inter), sans-serif',
                                      fontSize: '14px',
                                      fontWeight: 500,
                                      color: '#6b7280',
                                      margin: '0 0 4px 0',
                                      textTransform: 'none',
                                      letterSpacing: 'normal'
                                    }}
                                  >
                                    You can also ask
                                  </p>

                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '10px', width: '100%' }}>
                                    {(followupsToRender || []).slice(0, turnChip ? 2 : 4).map((fk, fIdx) => {
                                      const labelText = activeContext.labels[fk] || PREDICTION_MARKET_LABELS[fk] || fk;
                                      return (
                                        <button
                                          key={`${fk}-${fIdx}`}
                                          type="button"
                                          onClick={() => handleSelectFollowup(labelText, fk)}
                                          style={{
                                            display: 'inline-block',
                                            padding: '0',
                                            border: 0,
                                            background: 'transparent',
                                            fontFamily: 'var(--font-inter), sans-serif',
                                            fontSize: '14px',
                                            fontWeight: 400,
                                            color: '#333333',
                                            cursor: 'pointer',
                                            letterSpacing: 'normal',
                                            textTransform: 'none',
                                            textAlign: 'left',
                                            lineHeight: 1.8,
                                            transition: 'color .16s ease, transform .16s ease, opacity .16s ease',
                                            animation: 'smoothFadeIn 0.35s cubic-bezier(0.16, 1, 0.3, 1) both',
                                            animationDelay: `${(fIdx * 0.05).toFixed(2)}s`
                                          }}
                                          onMouseEnter={(e) => {
                                            (e.currentTarget as HTMLElement).style.color = '#6720ff';
                                            (e.currentTarget as HTMLElement).style.transform = 'translateX(2px)';
                                          }}
                                          onMouseLeave={(e) => {
                                            (e.currentTarget as HTMLElement).style.color = '#333333';
                                            (e.currentTarget as HTMLElement).style.transform = 'translateX(0px)';
                                          }}
                                        >
                                          {/* Dashed underline on the words only, line by line when it wraps */}
                                          <span
                                            style={{
                                              fontFamily: 'var(--font-inter), sans-serif',
                                              color: 'inherit',
                                              lineHeight: 1.8,
                                              textDecorationLine: 'underline',
                                              textDecorationStyle: 'dashed',
                                              textDecorationColor: '#d1d5db',
                                              textDecorationThickness: '1px',
                                              textUnderlineOffset: '5px'
                                            }}
                                          >
                                            {labelText}
                                          </span>
                                        </button>
                                      );
                                    })}
                                    {turnChip && (
                                      <div style={{ marginTop: "6px", width: "100%" }}>
                                        <YourTurnChip
                                          data={turnChip}
                                          theme="light"
                                          primaryColor="#6720ff"
                                          disabled={thinking || streaming}
                                          expanded={true}
                                          onAnswer={(text, payload) => {
                                            updateYourTurn(null);
                                            sendQuery(text, undefined, undefined, false, QUERY_TYPE.QUALIFICATION_OPTION, payload);
                                          }}
                                          onChooseCustom={(data) => {
                                            setCustomFor(data);
                                            if (data) inputRef.current?.focus();
                                          }}
                                        />
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </>
                        )}
                      </div>
                  )}
                </div>
              ))}

              {/* Email Summary Card */}
              {showShare && (
                <div
                  className="adv-share"
                  style={{
                    border: '1px solid #eeedf2',
                    borderRadius: '18px',
                    background: '#ffffff',
                    boxShadow: '0 10px 30px -14px rgba(30, 20, 70, 0.14)',
                    padding: shareOpen ? (isMobile ? '14px' : '16px') : 0,
                    animation: 'smoothFadeIn 0.4s cubic-bezier(0.16, 1, 0.3, 1) both'
                  }}
                >
                  {!shareOpen ? (
                    <button
                      type="button"
                      className="adv-share-toggle"
                      onClick={() => setShareOpen(true)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: isMobile ? '10px' : '14px',
                        width: '100%',
                        padding: isMobile ? '14px' : '16px',
                        border: 0,
                        borderRadius: '18px',
                        background: 'transparent',
                        cursor: 'pointer',
                        fontFamily: 'var(--font-inter), sans-serif',
                        textAlign: 'left'
                      }}
                    >
                      {shareHeading}
                      <span style={{ flex: '0 0 auto', marginRight: isMobile ? '2px' : '10px', fontSize: '14px', fontWeight: 600, color: BRAND, whiteSpace: 'nowrap' }}>
                        Send it <span aria-hidden="true">→</span>
                      </span>
                    </button>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', animation: 'fadeIn .25s ease both' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        {shareHeading}
                        <button
                          type="button"
                          className="adv-icon-btn"
                          onClick={() => setShareOpen(false)}
                          aria-label="Close"
                          title="Close"
                          style={{
                            width: '32px',
                            height: '32px',
                            flex: '0 0 auto',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            border: 0,
                            borderRadius: '8px',
                            background: 'transparent',
                            color: '#5f5c6b',
                            cursor: 'pointer',
                            padding: 0
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>

                      {shareSent ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: '10px 16px',
                            padding: '12px 14px',
                            borderRadius: '12px',
                            background: '#f4f2f8'
                          }}
                        >
                          <span style={{ fontSize: '14px', color: '#241f3b', lineHeight: 1.5 }}>
                            Summary on its way to <strong style={{ color: BRAND }}>{shareEmail}</strong>. Reply to that email if you would like our team to pick it up from there.
                          </span>
                          <button
                            type="button"
                            onClick={() => setShareSent(false)}
                            style={{
                              border: 0,
                              background: 'transparent',
                              color: BRAND,
                              fontSize: '13px',
                              fontWeight: 600,
                              cursor: 'pointer',
                              fontFamily: 'inherit',
                              padding: 0,
                              flex: '0 0 auto'
                            }}
                          >
                            Send another
                          </button>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
                            gap: '10px'
                          }}
                        >
                          <input
                            type="text"
                            className="adv-share-input"
                            placeholder="Your name"
                            aria-label="Your name"
                            autoComplete="name"
                            value={shareName}
                            onFocus={() => setShareFocus('name')}
                            onBlur={() => setShareFocus(null)}
                            onChange={(e) => setShareName(e.target.value)}
                            style={shareInputStyle(shareFocus === 'name')}
                          />
                          <input
                            type="email"
                            className="adv-share-input"
                            placeholder="Work email"
                            aria-label="Work email"
                            autoComplete="email"
                            value={shareEmail}
                            onFocus={() => setShareFocus('email')}
                            onBlur={() => setShareFocus(null)}
                            onChange={(e) => setShareEmail(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSendSummary();
                            }}
                            style={shareInputStyle(shareFocus === 'email')}
                          />
                          <button
                            type="button"
                            disabled={!isEmailValid || shareSending}
                            onClick={handleSendSummary}
                            style={{
                              height: '46px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              padding: '0 18px',
                              border: 0,
                              borderRadius: '12px',
                              background: isEmailValid && !shareSending ? BRAND : '#d6d4de',
                              color: '#ffffff',
                              fontFamily: 'inherit',
                              fontSize: '14px',
                              fontWeight: 600,
                              cursor: isEmailValid && !shareSending ? 'pointer' : 'default',
                              transition: 'background .16s ease'
                            }}
                          >
                            {shareSending ? 'Sending…' : <>Send summary <span aria-hidden="true">→</span></>}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Bot loading skeleton state matching compact view */}
              {thinking && !streaming && (
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '640px', animation: 'smoothFadeIn .3s cubic-bezier(0.16, 1, 0.3, 1) both' }}>
                  {/* Bot typing indicator row */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                        <div
                          className="vw-animate-bounce"
                          style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            backgroundColor: '#6720ff',
                            animation: 'bounce 1s infinite',
                            animationDelay: '0ms'
                          }}
                        />
                        <div
                          className="vw-animate-bounce"
                          style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            backgroundColor: '#6720ff',
                            animation: 'bounce 1s infinite',
                            animationDelay: '150ms'
                          }}
                        />
                        <div
                          className="vw-animate-bounce"
                          style={{
                            width: '7px',
                            height: '7px',
                            borderRadius: '50%',
                            backgroundColor: '#6720ff',
                            animation: 'bounce 1s infinite',
                            animationDelay: '300ms'
                          }}
                        />
                      </div>
                      <span style={{ fontSize: '14px', color: '#4b5563', fontWeight: 500 }}>
                        {loadingMessages[loadingMessageIndex]}
                      </span>
                    </div>

                    {/* Loading skeleton bars */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '9px', width: '100%' }}>
                      <div
                        className="vw-animate-pulse"
                        style={{
                          height: '13px',
                          borderRadius: '9999px',
                          width: '100%',
                          backgroundColor: '#e5e7eb',
                          animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                        }}
                      />
                      <div
                        className="vw-animate-pulse"
                        style={{
                          height: '13px',
                          borderRadius: '9999px',
                          width: '100%',
                          backgroundColor: '#e5e7eb',
                          animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                          animationDelay: '75ms'
                        }}
                      />
                      <div
                        className="vw-animate-pulse"
                        style={{
                          height: '13px',
                          borderRadius: '9999px',
                          width: '75%',
                          backgroundColor: '#e5e7eb',
                          animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                          animationDelay: '150ms'
                        }}
                      />
                    </div>
                  </div>
              )}
            </div>
          )}
        </div>



        {/* Bottom composer: always on phones, on desktop once the chat has started */}
        {(isMobile || hasMessages) && (
          <div
            style={{
              flex: '0 0 auto',
              position: 'relative',
              zIndex: 50,
              background: '#ffffff',
              padding: isMobile ? '6px 14px calc(12px + env(safe-area-inset-bottom, 0px))' : '6px 20px 18px'
            }}
          >
            <div style={{ maxWidth: '768px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {isMobile && isEmpty && (
                <div className="adv-chips" style={{ display: 'flex', gap: '8px', overflowX: 'auto', margin: '0 -14px', padding: '2px 14px' }}>
                  {activeContext.suggestions.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => handleSelectSuggestion(s)}
                      style={{
                        flexShrink: 0,
                        width: '260px',
                        padding: '12px 16px',
                        borderRadius: '18px',
                        border: '1px solid #eeedf2',
                        background: '#f9f9fb',
                        fontFamily: 'var(--font-inter), sans-serif',
                        fontSize: '16px',
                        color: '#111018',
                        cursor: 'pointer',
                        textAlign: 'left'
                      }}
                    >
                      <span className="adv-chip-text">{s.text}</span>
                    </button>
                  ))}
                </div>
              )}
              {composer}
            </div>
          </div>
        )}
          <style dangerouslySetInnerHTML={{ __html: `
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes fadeUp {
              from { opacity: 0; transform: translateY(12px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes smoothFadeIn {
              from { opacity: 0; transform: translateY(6px); }
              to { opacity: 1; transform: translateY(0); }
            }
            @keyframes slideInLeft {
              from { transform: translateX(-100%); }
              to { transform: translateX(0); }
            }
            @keyframes slideInRight {
              from { transform: translateX(100%); }
              to { transform: translateX(0); }
            }
            @keyframes drift {
              0%, 100% { transform: translate(0, 0); }
              25% { transform: translate(30px, -20px); }
              50% { transform: translate(-20px, 15px); }
              75% { transform: translate(15px, 10px); }
            }
            @keyframes pulseRing {
              0% { box-shadow: 0 0 0 0 rgba(103, 32, 255, 0.45); }
              70% { box-shadow: 0 0 0 8px rgba(103, 32, 255, 0); }
              100% { box-shadow: 0 0 0 0 rgba(103, 32, 255, 0); }
            }
            @keyframes caret {
              0%, 50% { opacity: 1; }
              51%, 100% { opacity: 0; }
            }
            @keyframes bounce {
              0%, 80%, 100% { transform: translateY(0); }
              40% { transform: translateY(-6px); }
            }
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
            /* Slim neutral scrollbar, no brand colour. */
            .vw-chat-messages-container {
              scrollbar-width: thin;
              scrollbar-color: rgba(22, 19, 31, 0.16) transparent;
            }
            .vw-chat-messages-container::-webkit-scrollbar {
              width: 4px;
            }
            .vw-chat-messages-container::-webkit-scrollbar-track {
              background: transparent;
            }
            .vw-chat-messages-container::-webkit-scrollbar-thumb {
              background: rgba(22, 19, 31, 0.16);
              border-radius: 4px;
            }
            .vw-chat-messages-container::-webkit-scrollbar-thumb:hover {
              background: rgba(22, 19, 31, 0.28);
            }
            .advisor-workspace .adv-row:hover,
            .advisor-workspace .adv-icon-btn:hover:not(:disabled) { background: #efeef3 !important; }
            .advisor-workspace .adv-sugg:hover { background: #f6f5f9 !important; color: #111018 !important; }
            .advisor-workspace .adv-cta:hover { background: #4a0fc2 !important; box-shadow: 0 0 0 3px #e4dafd; }
            .advisor-workspace .adv-small-link:hover { color: #6720ff !important; }
            .advisor-workspace .adv-rail-handle {
              position: absolute;
              top: 0;
              bottom: 0;
              right: -5px;
              width: 10px;
              z-index: 5;
              cursor: col-resize;
              touch-action: none;
            }
            .advisor-workspace .adv-rail-handle::after {
              content: '';
              position: absolute;
              top: 0;
              bottom: 0;
              left: 4px;
              width: 2px;
              background: transparent;
              transition: background .15s ease;
            }
            .advisor-workspace .adv-has-tip { position: relative; overflow: visible; }
            .advisor-workspace .adv-tip {
              position: absolute;
              left: calc(100% + 10px);
              top: 50%;
              transform: translateY(-50%);
              display: inline-flex;
              align-items: center;
              gap: 8px;
              padding: 6px 10px;
              border-radius: 8px;
              background: #111018;
              color: #ffffff;
              font-family: var(--font-inter), sans-serif;
              font-size: 13px;
              font-weight: 600;
              line-height: 1.2;
              white-space: nowrap;
              opacity: 0;
              pointer-events: none;
              transition: opacity .12s ease;
              z-index: 80;
            }
            .advisor-workspace .adv-tip--below {
              left: auto;
              right: 0;
              top: calc(100% + 6px);
              transform: none;
            }
            .advisor-workspace .adv-tip kbd {
              padding: 1px 6px;
              border-radius: 5px;
              background: rgba(255, 255, 255, 0.16);
              font-family: inherit;
              font-size: 12px;
              font-weight: 500;
            }
            .advisor-workspace .adv-has-tip:hover .adv-tip,
            .advisor-workspace .adv-has-tip:focus-visible .adv-tip { opacity: 1; transition-delay: .25s; }
            .advisor-workspace .adv-strip-toggle { display: none; }
            .advisor-workspace .adv-strip-logo:hover .adv-strip-mark,
            .advisor-workspace .adv-strip-logo:focus-visible .adv-strip-mark { display: none; }
            .advisor-workspace .adv-strip-logo:hover .adv-strip-toggle,
            .advisor-workspace .adv-strip-logo:focus-visible .adv-strip-toggle { display: inline-flex; }
            .advisor-workspace .adv-rail-handle:hover::after,
            .advisor-workspace .adv-rail-handle.is-dragging::after { background: #e4e3ea; }
            .advisor-workspace .adv-share-toggle:hover { background: #faf9fc !important; }
            .advisor-workspace .adv-share-input,
            #vinfotech-chat-widget-root .adv-share-input {
              width: 100% !important;
              height: 46px !important;
              box-sizing: border-box !important;
              padding: 0 16px !important;
              border: 1px solid transparent !important;
              border-radius: 12px !important;
              background: #f4f2f8 !important;
              font-family: inherit !important;
              font-size: 15px !important;
              color: #111018 !important;
              outline: none !important;
              box-shadow: none !important;
              transition: border-color .16s ease, background .16s ease !important;
            }
            .advisor-workspace .adv-share-input::placeholder,
            #vinfotech-chat-widget-root .adv-share-input::placeholder {
              color: #8e8b99 !important;
              opacity: 1 !important;
            }
            .advisor-workspace .adv-share-input:focus,
            #vinfotech-chat-widget-root .adv-share-input:focus {
              border-color: #cdbcfa !important;
              background: #ffffff !important;
            }
            .advisor-workspace .adv-h1 { text-wrap: balance; }
            .advisor-workspace .adv-chips { scrollbar-width: none; }
            .advisor-workspace .adv-chips::-webkit-scrollbar { display: none; }
            .advisor-workspace .adv-chip-text {
              display: -webkit-box;
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 2;
              overflow: hidden;
              line-height: 1.35;
            }
            /* Short screens (unfolded phones held sideways): fit the drawer, Talk to sales included. */
            @media (max-height: 600px) {
              .advisor-workspace .adv-drawer { gap: 10px !important; padding-top: 6px !important; }
              .advisor-workspace .adv-drawer .adv-row { min-height: 34px !important; font-size: 15px !important; }
              .advisor-workspace .adv-drawer .adv-topic-name { padding-top: 6px !important; padding-bottom: 6px !important; }
              .advisor-workspace .adv-drawer > button { min-height: 42px !important; }
            }
            /* Short laptop screens: keep the welcome screen inside the window. */
            @media (min-width: 880px) and (max-height: 760px) {
              .advisor-workspace .adv-empty { gap: 20px !important; padding-bottom: 4vh !important; }
              .advisor-workspace .adv-sugg { min-height: 46px !important; padding: 8px 16px !important; }
            }
            .advisor-workspace {
              font-family: var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
            }
            /* Scope-reset buttons that are not explicitly marked with .btn-condensed or .is-btn */
            .advisor-workspace button:not(.btn-condensed):not(.is-btn) {
              font-family: var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
              text-transform: none !important;
              letter-spacing: normal !important;
              line-height: normal !important;
              text-align: left;
            }
            .advisor-workspace button:not(.btn-condensed):not(.is-btn) *:not(.btn-condensed) {
              text-transform: none !important;
              letter-spacing: normal !important;
            }
            .advisor-workspace .btn-condensed {
              font-family: var(--font-favorit-condensed), sans-serif !important;
              text-transform: uppercase !important;
              letter-spacing: 2.29px !important;
            }
            .advisor-textarea {
              font-family: var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
              overflow-y: hidden !important;
              scrollbar-width: none !important;
              -ms-overflow-style: none !important;
            }
            .advisor-textarea::-webkit-scrollbar {
              display: none !important;
              width: 0 !important;
              height: 0 !important;
            }
            .advisor-textarea::placeholder {
              color: #9a98a3 !important;
              font-family: var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
              font-size: inherit !important;
              font-weight: 400 !important;
              opacity: 1 !important;
            }
          `}} />
      </main>
    </div>
  );
};


export { ExpandedAdvisorView as DedicatedAdvisorView };
